import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';

import { componentIds } from 'utilities/constants';
import { LOGOUT } from 'utilities/routes';
import { useGetComponent } from './useGetComponent';

interface IdleTimerContextProps {
  pauseTimer: () => void;
  resumeTimer: () => void;
}

export const IdleTimerContext = createContext<IdleTimerContextProps>({
  pauseTimer: () => ({}),
  resumeTimer: () => ({}),
});

export const useIdleTimerContext = () => useContext(IdleTimerContext);

export const useIdleTimerModalFunctionality = () => {
  const navigate = useNavigate();

  const [inactivityModalOpen, setInactivityModalOpen] =
    useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<string>('');

  const { data: componentLocale, loading: componentLoading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.INACTIVITY_MODAL,
  });

  const handleLogOut = useCallback(() => {
    setInactivityModalOpen(false);
    navigate(LOGOUT);
  }, [navigate]);

  const handleStillHere = useCallback(() => {
    setInactivityModalOpen(false);
  }, []);

  const { getRemainingTime, reset, getLastActiveTime, pause, resume } =
    useIdleTimer({
      timeout: 20 * 60 * 1000,
      promptBeforeIdle: 5 * 60 * 1000 + 1,
      throttle: 500,
      crossTab: true,
      leaderElection: true,
      syncTimers: 200,

      onPrompt: () => {
        setInactivityModalOpen(true);
      },
      onIdle: () => {
        handleLogOut();
      },
      onActive: (event) => {
        console.log('Active event:', event?.type);
        setInactivityModalOpen(false);
      },
    });

  useEffect(() => {
    if (!inactivityModalOpen) return;

    const updateRemainingTime = () => {
      const remaining = getRemainingTime();
      if (remaining <= 0) return;

      const minutes = Math.floor(remaining / 60_000);
      const seconds = Math.floor((remaining % 60_000) / 1000);

      const abbr = componentLocale?.minutesAbbreviation || 'min';
      setRemainingTime(
        `${minutes}:${seconds.toString().padStart(2, '0')} ${abbr}`,
      );
    };

    updateRemainingTime();

    const interval = setInterval(updateRemainingTime, 500);
    return () => clearInterval(interval);
  }, [inactivityModalOpen, getRemainingTime, componentLocale]);

  const onStayLoggedIn = () => {
    reset();
    handleStillHere();
  };

  return {
    inactivityModalOpen,
    handleStillHere: onStayLoggedIn,
    handleLogOut,
    componentLoading,
    componentLocale,
    remainingTime,
    getLastActiveTime,
    pause,
    resume,
  };
};
